import { Injectable } from '@angular/core';
import * as L from 'leaflet';
import { GisdataService } from 'src/app/main/services/gisdata.service';
import { AgricultureMapConstants, LAYER_TYPES } from 'src/app/main/constants/dims-map-constants';

// STATE.MIN = 6, so MIN - 1 = 5, matching Temperature module constants exactly
const STATE_VECTOR_DEF = {
  source: 'INDIA',
  name: 'State Boundary',
  layer_name: 'State Boundary',
  layeroptions_name: 'HEATWAVE:ALL_INDIA_STATE_UPDATED',
  layeroptions_pane: 'depth',
  layeroptions_min_zoom: 0,
  layeroptions_max_zoom: 22,
  group: 'boundaries',
  group_type: 'radio-deselect',
  layerServiceType: LAYER_TYPES.VECTOR_LAYER,
  type: LAYER_TYPES.VECTOR_LAYER,
  cql_filter_value: null,
  cql_filter: '1=1',
  environment_server_url: 'COMMON_GEOSERVER_URL',
  applycql_filter: true,
  visible: true,
  displayName: 'State',
  dashboardView: 'INDIA'
};

const STATE_LABELS_DEF = {
  source: 'INDIA',
  name: 'State Boundary Labels',
  layer_name: 'State Boundary Labels',
  layeroptions_name: 'HEATWAVE:ALL_INDIA_STATE_UPDATED',
  layeroptions_pane: 'depth',
  layeroptions_min_zoom: 5,
  layeroptions_max_zoom: 22,
  group: 'boundaries',
  group_type: 'radio-deselect',
  layerServiceType: LAYER_TYPES.WMS_LAYER,
  type: LAYER_TYPES.WMS_LAYER,
  cql_filter: '1=1',
  environment_server_url: 'COMMON_GEOSERVER_URL',
  layeroptions_styles: 'HEATWAVE:INDIA_STATE_NAMES_UPDATED',
  crs: 'EPSG4326',
  visible: true,
  displayName: 'State'
};

// District label WMS: text names for districts, shown when a district is selected
const DISTRICT_LABELS_DEF = {
  source: 'INDIA',
  name: 'District Boundary Labels',
  layer_name: 'District Boundary Labels',
  layeroptions_name: 'HEATWAVE:ALL_INDIA_DISTRICT_UPDATED',
  layeroptions_pane: 'depth',
  layeroptions_min_zoom: 8,
  layeroptions_max_zoom: 22,
  group: 'Labels',
  group_type: 'radio-deselect',
  layerServiceType: LAYER_TYPES.WMS_LAYER,
  type: LAYER_TYPES.WMS_LAYER,
  cql_filter: '1=1',
  environment_server_url: 'COMMON_GEOSERVER_URL',
  layeroptions_styles: 'HEATWAVE:ALL_INDIA_DISTRICT_NAMES_UPDATED',
  crs: 'EPSG4326',
  visible: true,
  displayName: 'District'
};

// District boundary: becomes visible when zoomed into a state (zoom ≥ 5)
const DISTRICT_VECTOR_DEF = {
  source: 'INDIA',
  name: 'District Boundary',
  layer_name: 'District Boundary',
  layeroptions_name: 'HEATWAVE:ALL_INDIA_DISTRICT_UPDATED',
  layeroptions_pane: 'district',
  layeroptions_min_zoom: 5,
  layeroptions_max_zoom: 22,
  group: 'boundaries',
  group_type: 'radio-deselect',
  layerServiceType: LAYER_TYPES.VECTOR_LAYER,
  type: LAYER_TYPES.VECTOR_LAYER,
  cql_filter_value: null,
  cql_filter: '1=1',
  environment_server_url: 'COMMON_GEOSERVER_URL',
  applycql_filter: true,
  visible: true,
  displayName: 'District',
  dashboardView: 'INDIA'
};

// Block boundary: becomes visible when zoomed into a district (zoom ≥ 8)
const BLOCK_VECTOR_DEF = {
  source: 'INDIA',
  name: 'Block Boundary',
  layer_name: 'Block Boundary',
  layeroptions_name: 'HEATWAVE:ALL_INDIA_BLOCKS_UPDATED',
  layeroptions_pane: 'state',
  layeroptions_min_zoom: 8,
  layeroptions_max_zoom: 22,
  group: 'boundaries',
  group_type: 'radio-deselect',
  layerServiceType: LAYER_TYPES.VECTOR_LAYER,
  type: LAYER_TYPES.VECTOR_LAYER,
  cql_filter_value: null,
  cql_filter: '1=1',
  environment_server_url: 'COMMON_GEOSERVER_URL',
  applycql_filter: true,
  visible: true,
  displayName: 'Block',
  dashboardView: 'INDIA'
};

// Mask WMS layer: world_view + admin boundary composite that greys out everything outside
// the selected state/district (mirrors COMMON_LAYER_LIST['world_view'] + addMaskLayer logic)
const MASK_WMS_BASE_DEF = {
  source: 'INDIA',
  displayName: 'Mask Layer',
  name: 'Mask Layer',
  visible: true,
  layeroptions_name: 'HEATWAVE:world_view',
  layer_name: 'Mask Layer',
  layeroptions_pane: 'mask',
  layeroptions_min_zoom: 0,
  layeroptions_max_zoom: 22,
  group: 'Mask Layer',
  group_type: 'radio-deselect',
  layerServiceType: LAYER_TYPES.WMS_LAYER,
  type: LAYER_TYPES.WMS_LAYER,
  cql_filter: null,
  cql_filter_value: null,
  layerFrom: AgricultureMapConstants.SOURCES.GEOSERVER,
  environment_server_url: 'COMMON_GEOSERVER_URL',
  layeroptions_styles: 'HEATWAVE:world_view_white,HEATWAVE:inverse_mask',
  crs: 'EPSG4326'
};

const REQUIRED_PANES = [
  { name: 'heatMap', zindex: '400' },
  { name: 'state', zindex: '401' },
  { name: 'district', zindex: '402' },
  { name: 'depth', zindex: '610' },
  { name: 'max', zindex: '620' },
  { name: 'mask', zindex: '700' }
];

// Minimal stub so setVECTOROverlays event handlers don't throw
const DUMMY_GIS_COMP = {
  otherOptions: { onLocationClick: false },
  params: { component: '' },
  componentType: ''
};

const DUMMY_PARAMS = { location: 'India&&', locType: 'COUNTRY' };

// Stub passed as gisMapComp to addGeoJsonLayersToMap (handleAdminPolygons ignores it)
const DUMMY_GIS_COMP_GEOJSON = {
  spinner: false,
  dataTabCSS: '',
  params: { locType: 'COUNTRY', component: '' }
};

@Injectable({ providedIn: 'root' })
export class SplitMapLayerService {

  // Cache raw WFS GeoJSON responses keyed by 'STATE:<uuid>' or 'DISTRICT:<uuid>'.
  // The HTTP call fires once; all split cards await the same cached promise.
  private selectionGeoJsonCache = new Map<string, Promise<any>>();

  constructor(private gisdataService: GisdataService) {}

  /**
   * Returns a promise for the raw WFS GeoJSON for the given locType + uuid.
   * Caches by key so concurrent callers (multiple map cards) share one HTTP request.
   * On fetch failure the cache entry is evicted so the next call retries.
   */
  private fetchSelectionGeoJson(locType: 'STATE' | 'DISTRICT', uuid: string): Promise<any> {
    const cacheKey = `${locType}:${uuid}`;
    if (this.selectionGeoJsonCache.has(cacheKey)) {
      return this.selectionGeoJsonCache.get(cacheKey);
    }
    const layerName = locType === 'STATE' ? 'ALL_INDIA_STATE_UPDATED' : 'ALL_INDIA_DISTRICT_UPDATED';
    const promise = this.gisdataService
      .getLayerWithFilter(layerName, `uuid='${uuid}'`, 'HEATWAVE', 'COMMON_GEOSERVER_URL', 'WFS')
      .toPromise()
      .catch(() => null);
    this.selectionGeoJsonCache.set(cacheKey, promise);
    // Evict on failure so the next attempt can retry
    promise.then(data => { if (!data) { this.selectionGeoJsonCache.delete(cacheKey); } });
    return promise;
  }

  /** Clears the GeoJSON cache (call when the overall selection is fully reset). */
  clearSelectionGeoJsonCache() {
    this.selectionGeoJsonCache.clear();
  }

  /**
   * Adds state + district boundary vector layers and state label WMS layer.
   * District layer uses min_zoom:5 so it only renders when zoomed into a state.
   * Returns all added Layer instances so the caller can remove them on destroy.
   */
  addBoundaryLayers(map: L.Map, paramId?: string): L.Layer[] {
    this.ensurePanes(map);
    const added: L.Layer[] = [];
    const boundaryColor = paramId === 'fog' ? '#00ffff' : '#000000';

    // ── 1. Vector: state boundary outlines ───────────────────────────────────
    const stateVectorDefs = { 'State Boundary': { ...STATE_VECTOR_DEF } };
    const stateOverlays = this.gisdataService.getApplicationBasedLayer(stateVectorDefs, LAYER_TYPES.VECTOR_LAYER);

    if (stateOverlays && stateOverlays['State Boundary']) {
      stateOverlays['State Boundary'].layerOptions.cql_filter = '1=1';
      const geoLayerName = stateOverlays['State Boundary'].layerOptions.layerName
        ? stateOverlays['State Boundary'].layerOptions.layerName.split(':')[1]
        : 'ALL_INDIA_STATE_UPDATED';
      stateOverlays['State Boundary'].layerOptions.vectorTileLayerStyles = {};
      stateOverlays['State Boundary'].layerOptions.vectorTileLayerStyles[geoLayerName] = () => ({
        color: boundaryColor,
        fill: false,
        weight: 0.5,
        opacity: 0.8,
        fillOpacity: 0
      });
    }

    const stateResult = this.gisdataService.setVECTOROverlays(
      map, stateOverlays, null, {}, DUMMY_PARAMS, null, DUMMY_GIS_COMP
    );
    Object.values(stateResult).forEach((l: any) => added.push(l));

    // ── 2. WMS: state name labels ─────────────────────────────────────────────
    // const wmsDefs = { 'State Boundary Labels': { ...STATE_LABELS_DEF } };
    // const wmsOverlays = this.gisdataService.getApplicationBasedLayer(wmsDefs, LAYER_TYPES.WMS_LAYER);

    // if (wmsOverlays && wmsOverlays['State Boundary Labels']) {
    //   wmsOverlays['State Boundary Labels'].layerOptions.cql_filter = '1=1';
    // }

    // const wmsResult = this.gisdataService.setWMSOverlays(map, wmsOverlays, {});
    // Object.values(wmsResult).forEach((l: any) => added.push(l));

    // ── 3. Vector: district boundary outlines (visible from zoom 5 / state level) ─
    // const distVectorDefs = { 'District Boundary': { ...DISTRICT_VECTOR_DEF } };
    // const distOverlays = this.gisdataService.getApplicationBasedLayer(distVectorDefs, LAYER_TYPES.VECTOR_LAYER);

    // if (distOverlays && distOverlays['District Boundary']) {
    //   distOverlays['District Boundary'].layerOptions.cql_filter = '1=1';
    //   const geoLayerName = distOverlays['District Boundary'].layerOptions.layerName
    //     ? distOverlays['District Boundary'].layerOptions.layerName.split(':')[1]
    //     : 'ALL_INDIA_DISTRICT_UPDATED';
    //   distOverlays['District Boundary'].layerOptions.vectorTileLayerStyles = {};
    //   distOverlays['District Boundary'].layerOptions.vectorTileLayerStyles[geoLayerName] = () => ({
    //     color: boundaryColor,
    //     fill: false,
    //     weight: 0.2,
    //     opacity: 0.7,
    //     fillOpacity: 0
    //   });
    // }

    // const distResult = this.gisdataService.setVECTOROverlays(
    //   map, distOverlays, null, {}, DUMMY_PARAMS, null, DUMMY_GIS_COMP
    // );
    // Object.values(distResult).forEach((l: any) => added.push(l));

    return added;
  }

  /**
   * Adds the district name label WMS layer (visible from zoom 5 / state level).
   * Called dynamically when the user selects a district in the header.
   * Returns the added layer so the caller can remove it when the district is deselected.
   */
  addDistrictLabelLayer(map: L.Map, distUUID?: string): L.Layer | null {
    this.ensurePanes(map);
    const wmsDefs = { 'District Boundary Labels': { ...DISTRICT_LABELS_DEF } };
    const wmsOverlays = this.gisdataService.getApplicationBasedLayer(wmsDefs, LAYER_TYPES.WMS_LAYER);

    if (!wmsOverlays || !wmsOverlays['District Boundary Labels']) { return null; }
    wmsOverlays['District Boundary Labels'].layerOptions.cql_filter = distUUID ? `uuid='${distUUID}'` : '1=1';

    const result = this.gisdataService.setWMSOverlays(map, wmsOverlays, {});
    const layers = Object.values(result);
    return layers.length ? (layers[0] as L.Layer) : null;
  }

  /**
   * Adds the block boundary vector layer (visible from zoom 8 / district level).
   * Only called for non-heat/feel params — heat/feel already render colored blocks
   * via SplitHeatLayerService. Returns the added layer for lifecycle tracking.
   */
  addBlockBoundaryLayer(map: L.Map, paramId?: string): L.Layer | null {
    this.ensurePanes(map);
    const boundaryColor = paramId === 'fog' ? '#00ffff' : '#000000';

    const blockVectorDefs = { 'Block Boundary': { ...BLOCK_VECTOR_DEF } };
    const blockOverlays = this.gisdataService.getApplicationBasedLayer(blockVectorDefs, LAYER_TYPES.VECTOR_LAYER);

    if (!blockOverlays || !blockOverlays['Block Boundary']) { return null; }

    blockOverlays['Block Boundary'].layerOptions.cql_filter = '1=1';
    const geoLayerName = blockOverlays['Block Boundary'].layerOptions.layerName
      ? blockOverlays['Block Boundary'].layerOptions.layerName.split(':')[1]
      : 'ALL_INDIA_BLOCKS_UPDATED';
    blockOverlays['Block Boundary'].layerOptions.vectorTileLayerStyles = {};
    blockOverlays['Block Boundary'].layerOptions.vectorTileLayerStyles[geoLayerName] = () => ({
      color: boundaryColor,
      fill: false,
      weight: 0.3,
      opacity: 0.6,
      fillOpacity: 0
    });

    const blockResult = this.gisdataService.setVECTOROverlays(
      map, blockOverlays, null, {}, DUMMY_PARAMS, null, DUMMY_GIS_COMP
    );
    const layers = Object.values(blockResult);
    return layers.length ? (layers[0] as L.Layer) : null;
  }

  /**
   * Called by addGeoJsonLayersToMap via the injector when rendering GeoJSON features.
   * Applies a highlight style to the selected state/district boundary polygon.
   */
  handleAdminPolygons(layer: any, mapObj: any, params: any, gisMapComp: any) {
    layer.setStyle({ color: '#000', fill: false, weight: 0.5, opacity: 1 });
  }

  /**
   * Renders the selected state or district boundary as a GeoJSON highlight layer.
   *
   * First call for a given uuid fires one WFS HTTP request and caches the raw GeoJSON.
   * All subsequent calls (other split-screen map cards) reuse the cached promise —
   * no additional HTTP requests are made. Each card still gets its own L.GeoJSON
   * instance created via addGeoJsonLayersToMap so it can be independently removed.
   *
   * locType 'STATE'    → HEATWAVE:ALL_INDIA_STATE_UPDATED,   pane 'depth'
   * locType 'DISTRICT' → HEATWAVE:ALL_INDIA_DISTRICT_UPDATED, pane 'max'
   */
  async addSelectionGeoJSONLayer(map: L.Map, locType: 'STATE' | 'DISTRICT', uuid: string): Promise<L.Layer | null> {
    if (!uuid || !map) { return null; }
    this.ensurePanes(map);

    const pane = locType === 'STATE' ? 'depth' : 'max';
    const layerOptions = {
      onEachFeature: 'handleAdminPolygons',
      pane,
      visible: true,
      restrictFitBounds: true
    };

    try {
      const geodata = await this.fetchSelectionGeoJson(locType, uuid);
      if (!geodata || !map) { return null; }

      const layer = this.gisdataService.addGeoJsonLayersToMap(
        geodata, map, layerOptions,
        { serviceType: SplitMapLayerService },
        DUMMY_GIS_COMP_GEOJSON
      );
      if (layer) { map.addLayer(layer); }
      return layer || null;
    } catch (e) {
      console.warn('SplitMapLayerService: addSelectionGeoJSONLayer failed', e);
      return null;
    }
  }

  /**
   * Adds a filtered district vector layer showing only districts within the selected state.
   * CQL filter state_uuid='{stateUUID}' is set in the raw config BEFORE getApplicationBasedLayer
   * so getVectorLayerURL bakes the correct filter into the tile URL.
   * Returns the added layer for lifecycle tracking.
   */
  addFilteredDistrictVectorLayer(map: L.Map, stateUUID: string, paramId?: string): L.Layer | null {
    if (!stateUUID || !map) { return null; }
    this.ensurePanes(map);
    const boundaryColor = paramId === 'fog' ? '#00ffff' : '#000000';

    const distVectorDefs = {
      'District Boundary': { ...DISTRICT_VECTOR_DEF, cql_filter: `state_uuid='${stateUUID}'` }
    };
    const distOverlays = this.gisdataService.getApplicationBasedLayer(distVectorDefs, LAYER_TYPES.VECTOR_LAYER);

    if (!distOverlays || !distOverlays['District Boundary']) { return null; }

    const geoLayerName = distOverlays['District Boundary'].layerOptions.layerName
      ? distOverlays['District Boundary'].layerOptions.layerName.split(':')[1]
      : 'ALL_INDIA_DISTRICT_UPDATED';
    distOverlays['District Boundary'].layerOptions.vectorTileLayerStyles = {};
    distOverlays['District Boundary'].layerOptions.vectorTileLayerStyles[geoLayerName] = () => ({
      color: boundaryColor,
      fill: false,
      weight: 0.2,
      opacity: 0.9,
      fillOpacity: 0
    });

    const result = this.gisdataService.setVECTOROverlays(
      map, distOverlays, null, {}, DUMMY_PARAMS, null, DUMMY_GIS_COMP
    );
    const layers = Object.values(result);
    return layers.length ? (layers[0] as L.Layer) : null;
  }

  /**
   * Adds a filtered block vector layer showing only blocks within the selected district.
   * CQL filter dist_uuid='{distUUID}' is set in the raw config BEFORE getApplicationBasedLayer
   * so getVectorLayerURL bakes the correct filter into the tile URL.
   * Returns the added layer for lifecycle tracking.
   */
  addFilteredBlockVectorLayer(map: L.Map, distUUID: string, paramId?: string): L.Layer | null {
    if (!distUUID || !map) { return null; }
    this.ensurePanes(map);
    const boundaryColor = paramId === 'fog' ? '#00ffff' : '#000000';

    const blockVectorDefs = {
      'Block Boundary': { ...BLOCK_VECTOR_DEF, cql_filter: `dist_uuid='${distUUID}'` }
    };
    const blockOverlays = this.gisdataService.getApplicationBasedLayer(blockVectorDefs, LAYER_TYPES.VECTOR_LAYER);

    if (!blockOverlays || !blockOverlays['Block Boundary']) { return null; }

    const geoLayerName = blockOverlays['Block Boundary'].layerOptions.layerName
      ? blockOverlays['Block Boundary'].layerOptions.layerName.split(':')[1]
      : 'ALL_INDIA_BLOCKS_UPDATED';
    blockOverlays['Block Boundary'].layerOptions.vectorTileLayerStyles = {};
    blockOverlays['Block Boundary'].layerOptions.vectorTileLayerStyles[geoLayerName] = () => ({
      color: boundaryColor,
      fill: false,
      weight: 0.2,
      opacity: 0.9,
      fillOpacity: 0
    });

    const result = this.gisdataService.setVECTOROverlays(
      map, blockOverlays, null, {}, DUMMY_PARAMS, null, DUMMY_GIS_COMP
    );
    const layers = Object.values(result);
    return layers.length ? (layers[0] as L.Layer) : null;
  }

  /**
   * Renders the world_view mask WMS layer for the selected state or district.
   * Mirrors addMaskLayer (GisdataService) + formatWmsLayersData 'world_view' else-branch:
   *   layeroptions_name = "HEATWAVE:world_view,<ADMIN_BOUNDARY_LAYER>"
   *   cql_filter        = "1=1;uuid='<uuid>'"  (semicolon separates the two WMS sublayers)
   * Greys out everything outside the selected area on the split screen map.
   */
  addSelectionMaskLayer(map: L.Map, locType: 'STATE' | 'DISTRICT', uuid: string): L.Layer | null {
    if (!uuid || !map) { return null; }
    this.ensurePanes(map);

    const adminLayerName = locType === 'STATE'
      ? 'HEATWAVE:ALL_INDIA_STATE_UPDATED'
      : 'HEATWAVE:ALL_INDIA_DISTRICT_UPDATED';

    const maskDef = {
      ...MASK_WMS_BASE_DEF,
      layeroptions_name: `HEATWAVE:world_view,${adminLayerName}`
    };

    const processed = this.gisdataService.getApplicationBasedLayer(
      { 'world_view': maskDef }, LAYER_TYPES.WMS_LAYER
    );
    if (!processed || !processed['world_view']) { return null; }

    // cql_filter: "1=1" for world_view layer, "uuid='<uuid>'" for the admin boundary layer
    // (same pattern as formatWmsLayersData else-branch for world_view)
    processed['world_view'].layerOptions.cql_filter = `1=1;uuid='${uuid}'`;

    const result = this.gisdataService.setWMSOverlays(map, processed, {});
    const layers = Object.values(result);
    return layers.length ? (layers[0] as L.Layer) : null;
  }

  private ensurePanes(map: L.Map) {
    REQUIRED_PANES.forEach(pane => {
      if (!map.getPane(pane.name)) {
        map.createPane(pane.name);
        map.getPane(pane.name).style.zIndex = pane.zindex;
      }
    });
  }
}
